import React, { useState, useEffect, useCallback } from 'react';
import { leadsApi, customersApi, employeesApi, citiesApi, dealersApi, serviceLeadsApi } from '@/services/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Users, MapPin, Search, Loader2, RefreshCw, CheckCircle,
  Phone, Database, Trash2, Copy, Activity, Zap, ArrowRightLeft, CalendarDays, AlertTriangle,
  Upload, Store, UserCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
});

export default function SpecialOpsTab() {
  // ─── Assign Unassigned ───
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);
  // Service lead activity lookup
  const [svcActivityOpen, setSvcActivityOpen] = useState(false);
  const [svcMobile, setSvcMobile] = useState('');
  const [svcLookupLoading, setSvcLookupLoading] = useState(false);
  const [svcLeads, setSvcLeads] = useState([]);
  const [svcSelectedId, setSvcSelectedId] = useState(null);
  const [svcSelectedDetail, setSvcSelectedDetail] = useState(null);

  const handleAssignUnassigned = async () => {
    setIsAssigning(true);
    try {
      const response = await leadsApi.assignUnassigned();
      setAssignResult(response.data);
      if (response.data?.assigned_count > 0) {
        toast.success(`${response.data.assigned_count} lead(s) auto-assigned to sales reps`);
      } else if (response.data?.failed_count > 0) {
        toast.warning(`${response.data.failed_count} lead(s) could not be assigned`);
      } else {
        toast.info('No unassigned leads found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to auto-assign leads');
    } finally {
      setIsAssigning(false);
    }
  };

  // ─── Create Demo Customer ───
  const [seedingData, setSeedingData] = useState(false);

  const handleSeedSampleData = async () => {
    setSeedingData(true);
    try {
      const res = await customersApi.seedSampleData();
      toast.success(`Sample customer "${res.data.customer_name}" created with ${res.data.inspections_created} packages!`);
    } catch (error) {
      toast.error('Failed to create sample data');
    } finally {
      setSeedingData(false);
    }
  };

  // ─── City Remap (Revamp City) ───
  const [isCityRemapOpen, setIsCityRemapOpen] = useState(false);
  const [cityRemapData, setCityRemapData] = useState({ fromCity: '', toCity: '', reassignToSalesRep: true });
  const [citySummary, setCitySummary] = useState([]);
  const [cities, setCities] = useState([]);
  const [isRemapping, setIsRemapping] = useState(false);
  const [isAutoRemapping, setIsAutoRemapping] = useState(false);
  const [isLoadingCitySummary, setIsLoadingCitySummary] = useState(false);
  const [remapMode, setRemapMode] = useState('auto');

  const fetchCitySummary = async () => {
    setIsLoadingCitySummary(true);
    try {
      const [summaryRes, citiesRes] = await Promise.all([
        leadsApi.getCitySummary(),
        citiesApi.getAll()
      ]);
      setCitySummary(summaryRes.data?.cities || []);
      setCities(citiesRes.data || []);
    } catch (error) {
      toast.error('Failed to load city summary');
    } finally {
      setIsLoadingCitySummary(false);
    }
  };

  const handleAutoRemapByAdId = async () => {
    setIsAutoRemapping(true);
    try {
      const response = await leadsApi.autoRemapByAdId(cityRemapData.reassignToSalesRep);
      const { remapped_count, reassigned_count, skipped_count } = response.data;
      if (remapped_count > 0) {
        toast.success(`Auto-remapped ${remapped_count} leads. ${reassigned_count} reassigned.`);
      } else {
        toast.info(`No leads needed remapping. ${skipped_count} already correct.`);
      }
      setIsCityRemapOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to auto-remap cities');
    } finally {
      setIsAutoRemapping(false);
    }
  };

  const handleCityRemap = async () => {
    if (!cityRemapData.fromCity || !cityRemapData.toCity) {
      toast.error('Please select both source and target cities');
      return;
    }
    if (cityRemapData.fromCity === cityRemapData.toCity) {
      toast.error('Source and target cities cannot be the same');
      return;
    }
    setIsRemapping(true);
    try {
      const response = await leadsApi.bulkRemapCity({
        from_city: cityRemapData.fromCity,
        to_city: cityRemapData.toCity,
        reassign_to_sales_rep: cityRemapData.reassignToSalesRep
      });
      toast.success(`${response.data.remapped_count} leads remapped from ${cityRemapData.fromCity} to ${cityRemapData.toCity}`);
      setIsCityRemapOpen(false);
      setCityRemapData({ fromCity: '', toCity: '', reassignToSalesRep: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remap cities');
    } finally {
      setIsRemapping(false);
    }
  };

  // ─── Investigate Lead ───
  const [isInvestigatorOpen, setIsInvestigatorOpen] = useState(false);
  const [investigatorSearch, setInvestigatorSearch] = useState('');
  const [investigatorSearchType, setInvestigatorSearchType] = useState('phone');
  const [investigatorResult, setInvestigatorResult] = useState(null);
  const [isInvestigating, setIsInvestigating] = useState(false);
  const [investigatorError, setInvestigatorError] = useState(null);
  const [investigatorTab, setInvestigatorTab] = useState('search');
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);

  const handleInvestigateLead = async () => {
    if (!investigatorSearch.trim()) {
      toast.error('Please enter a phone number or name to search');
      return;
    }
    setIsInvestigating(true);
    setInvestigatorResult(null);
    setInvestigatorError(null);
    try {
      const endpoint = investigatorSearchType === 'phone'
        ? `${API_URL}/api/leads/investigate/by-phone/${encodeURIComponent(investigatorSearch.trim())}`
        : `${API_URL}/api/leads/investigate/by-name/${encodeURIComponent(investigatorSearch.trim())}`;
      const response = await fetch(endpoint, { headers: getAuthHeader() });
      const data = await response.json();
      if (!response.ok) {
        setInvestigatorError(data.detail?.message || data.detail || 'Lead not found');
        return;
      }
      setInvestigatorResult(data);
    } catch {
      setInvestigatorError('Failed to investigate lead. Please try again.');
    } finally {
      setIsInvestigating(false);
    }
  };

  const handleDiagnoseSourceIssues = async () => {
    setIsDiagnosing(true);
    setDiagnosticData(null);
    try {
      const response = await fetch(`${API_URL}/api/leads/diagnose/source-issues`, { headers: getAuthHeader() });
      const data = await response.json();
      setDiagnosticData(data);
    } catch {
      toast.error('Failed to diagnose source issues');
    } finally {
      setIsDiagnosing(false);
    }
  };

  const handleFixSourceIssues = async (fixType, dryRun = true) => {
    setIsFixing(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/fix-source-issues?fix_type=${fixType}&dry_run=${dryRun}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (dryRun) {
        toast.info(`Preview: ${data.total_leads_affected} leads would be fixed`);
      } else {
        toast.success(`Fixed ${data.total_leads_affected} leads`);
        await handleDiagnoseSourceIssues();
      }
    } catch {
      toast.error('Failed to fix source issues');
    } finally {
      setIsFixing(false);
    }
  };

  const resetInvestigator = () => {
    setInvestigatorSearch('');
    setInvestigatorResult(null);
    setInvestigatorError(null);
    setDiagnosticData(null);
    setInvestigatorTab('search');
    setShowAuditTrail(false);
  };

  // ─── City View (Sales Exec Cities) ───
  const [isCityViewOpen, setIsCityViewOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  const fetchEmployees = async () => {
    setIsLoadingEmployees(true);
    try {
      const response = await employeesApi.getAll();
      setEmployees((response.data || []).filter(emp => emp.role_code === 'SALES_EXEC'));
    } catch {
      toast.error('Failed to load employees');
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // ─── Reassign Leads ───
  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const [reassignExecs, setReassignExecs] = useState([]);
  const [isLoadingExecs, setIsLoadingExecs] = useState(false);
  const [reassignFrom, setReassignFrom] = useState('');
  const [reassignTo, setReassignTo] = useState('');
  const [reassignDateFrom, setReassignDateFrom] = useState('');
  const [reassignDateTo, setReassignDateTo] = useState('');
  const [reassignStatuses, setReassignStatuses] = useState([]);
  const [reassignCity, setReassignCity] = useState('');
  const [reassignAll, setReassignAll] = useState(false);
  const [reassignPreview, setReassignPreview] = useState(null);
  const [isPreviewingReassign, setIsPreviewingReassign] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  const [reassignResult, setReassignResult] = useState(null);

  const LEAD_STATUSES = [
    'NEW LEAD', 'FOLLOW UP', 'WHATSAPP FOLLOW UP', 'HOT LEADS', 'INTERESTED',
    'NOT INTERESTED', 'RNR', 'RNR1', 'RNR2', 'RNR3', 'RCB WHATSAPP',
    'PAYMENT LINK SENT', 'PAID', 'DEAD LEAD', 'WRONG NUMBER', 'STOP',
    'OUT OF SERVICE AREA', 'PURCHASED FROM COMPETITOR'
  ];

  const fetchReassignExecs = async () => {
    setIsLoadingExecs(true);
    try {
      const response = await employeesApi.getAll();
      setReassignExecs((response.data || []).filter(emp => emp.role_code === 'SALES_EXEC'));
    } catch {
      toast.error('Failed to load sales executives');
    } finally {
      setIsLoadingExecs(false);
    }
  };

  const fromExec = reassignExecs.find(e => e.id === reassignFrom);
  const toExec = reassignExecs.find(e => e.id === reassignTo);
  const fromCities = fromExec?.assigned_cities || [];
  const toCities = toExec?.assigned_cities || [];

  const handlePreviewReassign = async () => {
    if (!reassignFrom || !reassignTo) { toast.error('Please select both executives'); return; }
    setIsPreviewingReassign(true);
    setReassignPreview(null);
    try {
      const params = new URLSearchParams({ from_user_id: reassignFrom, to_user_id: reassignTo });
      if (reassignAll) params.set('all_leads', 'true');
      if (reassignDateFrom) params.set('date_from', reassignDateFrom);
      if (reassignDateTo) params.set('date_to', reassignDateTo);
      if (reassignStatuses.length > 0) params.set('statuses', reassignStatuses.join(','));
      if (reassignCity) params.set('city', reassignCity);
      const response = await fetch(`${API_URL}/api/leads/bulk-reassign/preview?${params}`, { headers: getAuthHeader() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Preview failed');
      setReassignPreview(data);
    } catch (err) {
      toast.error(err.message || 'Failed to preview reassignment');
    } finally {
      setIsPreviewingReassign(false);
    }
  };

  const handleBulkReassign = async () => {
    if (!reassignFrom || !reassignTo) { toast.error('Please select both executives'); return; }
    setIsReassigning(true);
    setReassignResult(null);
    try {
      const payload = {
        from_user_id: reassignFrom,
        to_user_id: reassignTo,
        all_leads: reassignAll,
        dry_run: false,
      };
      if (!reassignAll) {
        if (reassignDateFrom) payload.date_from = reassignDateFrom;
        if (reassignDateTo) payload.date_to = reassignDateTo;
        if (reassignStatuses.length > 0) payload.statuses = reassignStatuses;
        if (reassignCity) payload.city = reassignCity;
      }
      const response = await fetch(`${API_URL}/api/leads/bulk-reassign`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Reassignment failed');
      setReassignResult(data);
      if (data.reassigned_count > 0) {
        toast.success(`Successfully reassigned ${data.reassigned_count} lead(s) to ${data.to_name}`);
      } else {
        toast.info('No leads were reassigned');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to reassign leads');
    } finally {
      setIsReassigning(false);
    }
  };

  const resetReassign = () => {
    setReassignFrom(''); setReassignTo('');
    setReassignDateFrom(''); setReassignDateTo('');
    setReassignStatuses([]); setReassignCity('');
    setReassignAll(false); setReassignPreview(null);
    setReassignResult(null);
  };

  const toggleStatus = (status) => {
    setReassignStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
    setReassignPreview(null);
  };

  // ─── Dealer Import Excel ───
  const [isDealerImportOpen, setIsDealerImportOpen] = useState(false);
  const [dealerImportFile, setDealerImportFile] = useState(null);
  const [isDealerImporting, setIsDealerImporting] = useState(false);

  const handleDealerImport = async () => {
    if (!dealerImportFile) return;
    setIsDealerImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', dealerImportFile);
      const res = await dealersApi.bulkImport(fd);
      toast.success(`Imported ${res.data.imported} dealers (${res.data.skipped} skipped)`);
      setIsDealerImportOpen(false);
      setDealerImportFile(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Dealer import failed'); }
    finally { setIsDealerImporting(false); }
  };

  // ─── Dealer Bulk Assign ───
  const [isDealerAssignOpen, setIsDealerAssignOpen] = useState(false);
  const [dealerAssignExecs, setDealerAssignExecs] = useState([]);
  const [dealerCities, setDealerCities] = useState([]);
  const [dealerAssignForm, setDealerAssignForm] = useState({ city: '', assigned_to: '', assigned_to_name: '' });
  const [isDealerAssigning, setIsDealerAssigning] = useState(false);

  const fetchDealerAssignData = async () => {
    try {
      const [execs, dealers] = await Promise.all([
        dealersApi.getAssignableEmployees(),
        dealersApi.getAll({ per_page: 1 }),
      ]);
      setDealerAssignExecs(execs.data || []);
      setDealerCities(dealers.data?.cities || []);
    } catch {}
  };

  const handleDealerBulkAssign = async () => {
    if (!dealerAssignForm.assigned_to) { toast.error('Select an executive'); return; }
    if (!dealerAssignForm.city) { toast.error('Select a city'); return; }
    setIsDealerAssigning(true);
    try {
      const res = await dealersApi.bulkAssign({
        city: dealerAssignForm.city,
        assigned_to: dealerAssignForm.assigned_to,
        assigned_to_name: dealerAssignForm.assigned_to_name,
      });
      toast.success(`Assigned ${res.data.modified_count} dealers to ${dealerAssignForm.assigned_to_name}`);
      setIsDealerAssignOpen(false);
      setDealerAssignForm({ city: '', assigned_to: '', assigned_to_name: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk assign failed'); }
    finally { setIsDealerAssigning(false); }
  };

  // ─── Force Complete Inspection (P0 escape hatch) ───
  // Opens a dialog where ops can search by vehicle plate or inspection UUID,
  // see the inspection's current state + OBD flags, and force-complete it
  // bypassing all mechanic-app client-side validations. Backend audits
  // the action with the user's name + reason. See test_34 regression suite.
  const [isForceCompleteOpen, setIsForceCompleteOpen] = useState(false);
  const [fcSearchTerm, setFcSearchTerm] = useState('');
  const [fcInspection, setFcInspection] = useState(null);
  const [fcReason, setFcReason] = useState('');
  const [fcSearching, setFcSearching] = useState(false);
  const [fcSubmitting, setFcSubmitting] = useState(false);
  const [fcConfirmShown, setFcConfirmShown] = useState(false);

  const resetForceComplete = () => {
    setFcSearchTerm('');
    setFcInspection(null);
    setFcReason('');
    setFcSearching(false);
    setFcSubmitting(false);
    setFcConfirmShown(false);
  };

  const handleFcSearch = async () => {
    const term = fcSearchTerm.trim();
    if (!term) {
      toast.error('Enter an inspection ID or vehicle number first.');
      return;
    }
    setFcSearching(true);
    setFcInspection(null);
    setFcConfirmShown(false);
    try {
      const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(term);
      let candidate = null;
      if (looksLikeUuid) {
        const r = await fetch(`${API_URL}/api/inspections/${term}`, { headers: getAuthHeader() });
        if (r.ok) candidate = await r.json();
      } else {
        const r = await fetch(`${API_URL}/api/inspections?search=${encodeURIComponent(term)}&limit=5`, {
          headers: getAuthHeader(),
        });
        if (r.ok) {
          const body = await r.json();
          const list = body?.inspections || body?.items || body || [];
          if (Array.isArray(list) && list.length > 0) {
            candidate = list.find(
              (i) => !['INSPECTION_COMPLETED', 'PUBLISHED', 'CANCELLED'].includes(i.inspection_status)
            ) || list[0];
          }
        }
      }
      if (!candidate) {
        toast.error(`No inspection found for "${term}".`);
        return;
      }
      setFcInspection(candidate);
    } catch (e) {
      toast.error(`Search failed: ${e.message}`);
    } finally {
      setFcSearching(false);
    }
  };

  const handleFcForceComplete = async () => {
    if (!fcInspection) return;
    if (!fcReason.trim() || fcReason.trim().length < 10) {
      toast.error('Please provide a meaningful reason (at least 10 characters).');
      return;
    }
    if (!fcConfirmShown) {
      setFcConfirmShown(true);
      return;
    }
    setFcSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/inspections/${fcInspection.id}/force-complete`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: fcReason.trim() }),
      });
      const body = await r.json();
      if (!r.ok) {
        toast.error(`Force-complete failed: ${body?.detail || r.statusText}`);
        setFcConfirmShown(false);
        return;
      }
      if (body.already_completed) {
        toast.info(`Inspection is already ${body.status}. No action taken.`);
      } else {
        toast.success('Inspection force-completed. AI report generation triggered.');
      }
      setFcInspection({ ...fcInspection, inspection_status: 'INSPECTION_COMPLETED' });
      setFcConfirmShown(false);
    } catch (e) {
      toast.error(`Force-complete failed: ${e.message}`);
      setFcConfirmShown(false);
    } finally {
      setFcSubmitting(false);
    }
  };

  const fcAlreadyTerminal = fcInspection &&
    (fcInspection.inspection_status === 'INSPECTION_COMPLETED' ||
     fcInspection.inspection_status === 'PUBLISHED');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Special Ops</h2>
        <p className="text-sm text-gray-500">Admin operations for leads, customers, and city management</p>
      </div>

      {/* Operations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Assign Unassigned */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-assign">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Assign Unassigned</h3>
              <p className="text-xs text-gray-500">Auto-assign leads to sales reps via round-robin</p>
            </div>
          </div>
          {assignResult && (
            <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Assigned:</span><strong className="text-emerald-600">{assignResult.assigned_count || 0}</strong></div>
              <div className="flex justify-between"><span className="text-gray-600">Failed:</span><strong className="text-red-600">{assignResult.failed_count || 0}</strong></div>
              <div className="flex justify-between"><span className="text-gray-600">Skipped:</span><strong className="text-gray-500">{assignResult.skipped_count || 0}</strong></div>
            </div>
          )}
          <Button
            onClick={handleAssignUnassigned}
            disabled={isAssigning}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="assign-unassigned-btn"
          >
            {isAssigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
            {isAssigning ? 'Assigning...' : 'Assign Unassigned'}
          </Button>
        </div>

        {/* Revamp City */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-remap">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <MapPin className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Revamp City</h3>
              <p className="text-xs text-gray-500">Remap lead cities (auto by AD ID or manual)</p>
            </div>
          </div>
          <Button
            onClick={() => { fetchCitySummary(); setIsCityRemapOpen(true); }}
            className="w-full bg-orange-500 hover:bg-orange-600"
            data-testid="revamp-city-btn"
          >
            <MapPin className="h-4 w-4 mr-2" />
            Revamp City
          </Button>
        </div>

        {/* Investigate Lead */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-investigate">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Search className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Investigate Lead</h3>
              <p className="text-xs text-gray-500">Search leads by phone/name, diagnose issues</p>
            </div>
          </div>
          <Button
            onClick={() => { resetInvestigator(); setIsInvestigatorOpen(true); }}
            className="w-full bg-purple-600 hover:bg-purple-700"
            data-testid="investigate-lead-btn"
          >
            <Search className="h-4 w-4 mr-2" />
            Investigate Lead
          </Button>
        </div>

        {/* Create Demo Customer */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-demo">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Database className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Create Demo Customer</h3>
              <p className="text-xs text-gray-500">Seed a sample customer with inspection data</p>
            </div>
          </div>
          <Button
            onClick={handleSeedSampleData}
            disabled={seedingData}
            variant="outline"
            className="w-full"
            data-testid="create-demo-btn"
          >
            {seedingData ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
            {seedingData ? 'Creating...' : 'Create Demo Customer'}
          </Button>
        </div>

        {/* City View */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-cityview">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <MapPin className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">City View</h3>
              <p className="text-xs text-gray-500">View city assignments per sales executive</p>
            </div>
          </div>
          <Button
            onClick={() => { fetchEmployees(); setIsCityViewOpen(true); }}
            variant="outline"
            className="w-full"
            data-testid="city-view-btn"
          >
            <MapPin className="h-4 w-4 mr-2" />
            City View
          </Button>
        </div>

        {/* Reassign Leads */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-reassign">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <ArrowRightLeft className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Reassign Leads</h3>
              <p className="text-xs text-gray-500">Bulk transfer leads between sales executives</p>
            </div>
          </div>
          <Button
            onClick={() => { resetReassign(); fetchReassignExecs(); setIsReassignOpen(true); }}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            data-testid="reassign-leads-btn"
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Reassign Leads
          </Button>
        </div>

        {/* Import Dealers (Excel) */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-dealer-import">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Upload className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Import Dealers</h3>
              <p className="text-xs text-gray-500">Bulk import dealers from Excel/CSV file</p>
            </div>
          </div>
          <Button
            onClick={() => setIsDealerImportOpen(true)}
            className="w-full bg-teal-600 hover:bg-teal-700"
            data-testid="dealer-import-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import Dealers
          </Button>
        </div>

        {/* Bulk Assign Dealers */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-dealer-assign">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-cyan-100 flex items-center justify-center">
              <UserCheck className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Bulk Assign Dealers</h3>
              <p className="text-xs text-gray-500">Assign dealers by city to an executive</p>
            </div>
          </div>
          <Button
            onClick={() => { fetchDealerAssignData(); setIsDealerAssignOpen(true); }}
            className="w-full bg-cyan-600 hover:bg-cyan-700"
            data-testid="dealer-bulk-assign-btn"
          >
            <UserCheck className="h-4 w-4 mr-2" />
            Bulk Assign Dealers
          </Button>
        </div>

        {/* Force Complete Inspection (P0 escape hatch) */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow" data-testid="special-ops-force-complete">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-rose-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Force Complete Inspection</h3>
              <p className="text-xs text-gray-500">Mechanic app stuck? Force-complete with audit trail</p>
            </div>
          </div>
          <Button
            onClick={() => { resetForceComplete(); setIsForceCompleteOpen(true); }}
            className="w-full bg-rose-600 hover:bg-rose-700"
            data-testid="force-complete-btn"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Force Complete
          </Button>
        </div>

        {/* Fetch Service Lead Activity by mobile */}
        <div className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow"
             data-testid="special-ops-svc-activity">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Search className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Fetch Service Lead Activity</h3>
              <p className="text-xs text-gray-500">Look up the full activity timeline for a customer&apos;s service lead by mobile</p>
            </div>
          </div>
          <Button
            onClick={() => setSvcActivityOpen(true)}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="open-svc-activity-lookup"
          >
            <Search className="h-4 w-4 mr-2" /> Open lookup
          </Button>
        </div>
      </div>

      {/* ═══ Dealer Import Modal ═══ */}
      <Dialog open={isDealerImportOpen} onOpenChange={setIsDealerImportOpen}>
        <DialogContent className="max-w-md" data-testid="dealer-import-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-teal-600" /> Import Dealers from Excel/CSV</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Upload a .xlsx or .csv file with dealer data.</p>
              <button onClick={() => {
                const token = localStorage.getItem('token');
                const baseUrl = process.env.REACT_APP_BACKEND_URL;
                window.open(`${baseUrl}/api/dealers/sample-import-template?token=${token}`, '_blank');
              }} className="text-sm text-blue-600 hover:text-blue-700 font-medium underline cursor-pointer" data-testid="dealer-download-sample-btn">
                Download Sample
              </button>
            </div>
            <Input type="file" accept=".xlsx,.xls,.csv" onChange={e => setDealerImportFile(e.target.files?.[0])} data-testid="dealer-import-file-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDealerImportOpen(false)}>Cancel</Button>
              <Button onClick={handleDealerImport} disabled={!dealerImportFile || isDealerImporting} data-testid="dealer-confirm-import-btn">
                {isDealerImporting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Import
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Dealer Bulk Assign Modal ═══ */}
      <Dialog open={isDealerAssignOpen} onOpenChange={setIsDealerAssignOpen}>
        <DialogContent className="max-w-md" data-testid="dealer-assign-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="h-5 w-5 text-cyan-600" /> Bulk Assign Dealers</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>City *</Label>
              <Select value={dealerAssignForm.city} onValueChange={v => setDealerAssignForm({ ...dealerAssignForm, city: v })}>
                <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent>
                  {dealerCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Assign To *</Label>
              <Select value={dealerAssignForm.assigned_to} onValueChange={v => {
                const emp = dealerAssignExecs.find(e => e.id === v);
                setDealerAssignForm({ ...dealerAssignForm, assigned_to: v, assigned_to_name: emp?.name || '' });
              }}>
                <SelectTrigger><SelectValue placeholder="Select executive" /></SelectTrigger>
                <SelectContent>
                  {dealerAssignExecs.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDealerAssignOpen(false)}>Cancel</Button>
              <Button onClick={handleDealerBulkAssign} disabled={isDealerAssigning} data-testid="dealer-confirm-assign-btn">
                {isDealerAssigning && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Assign
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ City Remap Modal ═══ */}
      <Dialog open={isCityRemapOpen} onOpenChange={setIsCityRemapOpen}>
        <DialogContent className="max-w-lg" data-testid="city-remap-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-orange-500" />
              City Remap
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Mode Toggle */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button onClick={() => setRemapMode('auto')} className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${remapMode === 'auto' ? 'bg-white shadow text-orange-600' : 'text-gray-600 hover:text-gray-900'}`}>
                Auto (by AD ID)
              </button>
              <button onClick={() => setRemapMode('manual')} className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${remapMode === 'manual' ? 'bg-white shadow text-orange-600' : 'text-gray-600 hover:text-gray-900'}`}>
                Manual
              </button>
            </div>

            {/* City Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Current Lead Distribution</h4>
              {isLoadingCitySummary ? (
                <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {citySummary.map((city) => (
                    <div key={city.city} className="flex justify-between items-center text-sm bg-white px-3 py-2 rounded-md border">
                      <span className="text-gray-700">{city.city}</span>
                      <span className="font-medium text-gray-900">{city.total_leads}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Auto Mode */}
            {remapMode === 'auto' && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-800 mb-2">Auto-Remap by AD ID</h4>
                  <p className="text-sm text-green-700 mb-3">Fix lead cities based on AD ID to City mappings.</p>
                  <ul className="text-xs text-green-600 space-y-1">
                    <li>Matches leads by ad_id or ad_name from AD City Mappings</li>
                    <li>Only updates leads where current city differs</li>
                  </ul>
                </div>
                <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
                  <Checkbox id="reassign-auto" checked={cityRemapData.reassignToSalesRep} onCheckedChange={(checked) => setCityRemapData({ ...cityRemapData, reassignToSalesRep: checked })} />
                  <div>
                    <Label htmlFor="reassign-auto" className="text-sm font-medium cursor-pointer">Reassign to Sales Reps</Label>
                    <p className="text-xs text-gray-500">Auto-reassign via round-robin in corrected city</p>
                  </div>
                </div>
              </>
            )}

            {/* Manual Mode */}
            {remapMode === 'manual' && (
              <>
                <div className="space-y-2">
                  <Label>From City (Source)</Label>
                  <Select value={cityRemapData.fromCity} onValueChange={(value) => setCityRemapData({ ...cityRemapData, fromCity: value })}>
                    <SelectTrigger><SelectValue placeholder="Select source city" /></SelectTrigger>
                    <SelectContent>
                      {citySummary.filter(c => c.total_leads > 0).map((city) => (
                        <SelectItem key={city.city} value={city.city}>{city.city} ({city.total_leads} leads)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To City (Target)</Label>
                  <Select value={cityRemapData.toCity} onValueChange={(value) => setCityRemapData({ ...cityRemapData, toCity: value })}>
                    <SelectTrigger><SelectValue placeholder="Select target city" /></SelectTrigger>
                    <SelectContent>
                      {cities.map((city) => (
                        <SelectItem key={city.id || city.name} value={city.name}>{city.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
                  <Checkbox id="reassign-manual" checked={cityRemapData.reassignToSalesRep} onCheckedChange={(checked) => setCityRemapData({ ...cityRemapData, reassignToSalesRep: checked })} />
                  <div>
                    <Label htmlFor="reassign-manual" className="text-sm font-medium cursor-pointer">Reassign to Sales Reps</Label>
                    <p className="text-xs text-gray-500">Auto-reassign via round-robin in new city</p>
                  </div>
                </div>
                {cityRemapData.fromCity && cityRemapData.toCity && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <p className="text-amber-800">
                      <strong>Warning:</strong> This will move ALL leads from <strong>{cityRemapData.fromCity}</strong> to <strong>{cityRemapData.toCity}</strong>.
                      {cityRemapData.reassignToSalesRep && ' Leads will also be reassigned.'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsCityRemapOpen(false)} disabled={isRemapping || isAutoRemapping}>Cancel</Button>
            {remapMode === 'auto' && (
              <Button onClick={handleAutoRemapByAdId} disabled={isAutoRemapping} className="bg-green-600 hover:bg-green-700" data-testid="auto-remap-btn">
                {isAutoRemapping ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Auto-Remapping...</> : <><CheckCircle className="h-4 w-4 mr-2" /> Auto-Remap by AD ID</>}
              </Button>
            )}
            {remapMode === 'manual' && (
              <Button onClick={handleCityRemap} disabled={isRemapping || !cityRemapData.fromCity || !cityRemapData.toCity} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-remap-btn">
                {isRemapping ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Remapping...</> : <><RefreshCw className="h-4 w-4 mr-2" /> Remap Leads</>}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Investigate Lead Modal ═══ */}
      <Dialog open={isInvestigatorOpen} onOpenChange={setIsInvestigatorOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto" data-testid="investigator-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Search className="h-5 w-5 text-purple-600" />
              Lead Investigator
            </DialogTitle>
          </DialogHeader>

          {/* Tab Toggle */}
          <div className="flex gap-2 border-b pb-3">
            <button onClick={() => setInvestigatorTab('search')} className={`flex-1 py-2 px-4 rounded-t-lg font-medium transition-all ${investigatorTab === 'search' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Search className="h-4 w-4 inline mr-2" /> Search Lead
            </button>
            <button onClick={() => { setInvestigatorTab('diagnose'); if (!diagnosticData) handleDiagnoseSourceIssues(); }} className={`flex-1 py-2 px-4 rounded-t-lg font-medium transition-all ${investigatorTab === 'diagnose' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Activity className="h-4 w-4 inline mr-2" /> Diagnose Issues
            </button>
          </div>

          <div className="space-y-4 py-4">
            {/* SEARCH TAB */}
            {investigatorTab === 'search' && (
              <>
                <div className="flex gap-2">
                  <button onClick={() => { setInvestigatorSearchType('phone'); setInvestigatorSearch(''); setInvestigatorResult(null); setInvestigatorError(null); }} className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${investigatorSearchType === 'phone' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                    <Phone className="h-4 w-4 inline mr-2" /> By Phone
                  </button>
                  <button onClick={() => { setInvestigatorSearchType('name'); setInvestigatorSearch(''); setInvestigatorResult(null); setInvestigatorError(null); }} className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${investigatorSearchType === 'name' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                    <Users className="h-4 w-4 inline mr-2" /> By Name
                  </button>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder={investigatorSearchType === 'phone' ? 'Enter phone number' : 'Enter lead name'}
                    value={investigatorSearch}
                    onChange={(e) => setInvestigatorSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleInvestigateLead()}
                    className="flex-1"
                    data-testid="investigator-search-input"
                  />
                  <Button onClick={handleInvestigateLead} disabled={isInvestigating || !investigatorSearch.trim()} className="bg-purple-600 hover:bg-purple-700" data-testid="investigate-btn">
                    {isInvestigating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
                {investigatorError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700" data-testid="investigator-error">
                    <p className="font-medium">{investigatorError}</p>
                  </div>
                )}
                {investigatorResult && (
                  <div className="space-y-4" data-testid="investigator-results">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="font-medium text-green-800 mb-2">Lead Found</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-600">Name:</span> <strong>{investigatorResult.name}</strong></div>
                        <div><span className="text-gray-600">Phone:</span> <strong>{investigatorResult.mobile}</strong></div>
                        <div><span className="text-gray-600">City:</span> <strong>{investigatorResult.city || 'N/A'}</strong></div>
                        <div><span className="text-gray-600">Status:</span> <strong>{investigatorResult.status}</strong></div>
                      </div>
                    </div>
                    <div className={`border rounded-lg p-4 ${investigatorResult.is_meta_lead ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                      <p className="font-medium mb-3 flex items-center gap-2">
                        {investigatorResult.is_meta_lead ? <><span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">META</span> Meta WhatsApp Lead</> : <><span className="bg-gray-600 text-white px-2 py-0.5 rounded text-xs">SOURCE</span> {investigatorResult.source || 'Unknown'}</>}
                      </p>
                      <div className="space-y-2 text-sm">
                        {[['Source', investigatorResult.source], ['AD ID', investigatorResult.ad_id], ['AD Name', investigatorResult.ad_name], ['Campaign ID', investigatorResult.campaign_id], ['Google Campaign', investigatorResult.google_campaign_id], [(investigatorResult.click_type || 'Click').toUpperCase(), investigatorResult.click_id], ['Attributed via', investigatorResult.attribution_source?.replace(/_/g, ' ')], ['Platform', investigatorResult.platform], ['Created At', investigatorResult.created_at ? new Date(investigatorResult.created_at).toLocaleString() : null]].map(([label, value]) => (
                          <div key={label} className="flex justify-between py-1 border-b border-dashed" data-testid={`investigate-row-${String(label).toLowerCase().replace(/\s+/g, '-')}`}>
                            <span className="text-gray-600">{label}:</span>
                            <span className="font-mono font-medium truncate max-w-[60%] text-right">{value || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {investigatorResult.other_leads?.length > 0 && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="investigate-other-leads">
                        <p className="font-medium mb-2 text-slate-700">This number has {investigatorResult.other_leads.length} other lead{investigatorResult.other_leads.length > 1 ? 's' : ''}</p>
                        <div className="space-y-1 text-xs">
                          {investigatorResult.other_leads.map((l) => (
                            <div key={l.id} className="flex justify-between gap-3 py-1 border-b border-dashed">
                              <span className="text-gray-600">{l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'} · {l.source || 'Unknown'} · {l.status || '—'}</span>
                              <span className="font-mono">{l.ad_id || (l.attribution_source ? l.attribution_source.replace(/_/g, ' ') : 'no ad')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {investigatorResult.ctwa_data && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="font-medium mb-2 text-amber-800">Click-to-WhatsApp Ad Data</p>
                        <div className="space-y-1 text-sm">
                          {Object.entries(investigatorResult.ctwa_data).filter(([, v]) => v).map(([key, value]) => (
                            <div key={key}><span className="text-gray-600">{key}:</span> <span className="font-medium">{String(value)}</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                    {investigatorResult.webhook_audit && (
                      <div className="border-t pt-4">
                        <button onClick={() => setShowAuditTrail(!showAuditTrail)} className="w-full flex items-center justify-between py-2 px-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium">
                          <span className="flex items-center gap-2"><Activity className="h-4 w-4" /> Webhook Audit Trail</span>
                          <span>{showAuditTrail ? '\u25B2' : '\u25BC'}</span>
                        </button>
                        {showAuditTrail && (
                          <div className="mt-3 space-y-4 text-xs">
                            <div className="bg-slate-800 text-slate-100 rounded-lg p-3 overflow-x-auto">
                              <h5 className="font-bold text-amber-400 mb-2">RAW TWILIO PARAMS</h5>
                              <pre className="whitespace-pre-wrap">{JSON.stringify(investigatorResult.webhook_audit.raw_twilio_params || {}, null, 2)}</pre>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <h5 className="font-bold text-blue-800 mb-2">PARSED STANDARD FIELDS</h5>
                              <div className="grid grid-cols-2 gap-1">
                                {Object.entries(investigatorResult.webhook_audit.parsed_standard_fields || {}).map(([key, value]) => (
                                  <div key={key} className="flex justify-between border-b border-dashed border-blue-200 py-1">
                                    <span className="text-blue-600">{key}:</span>
                                    <span className={`font-mono ${value ? 'text-green-700' : 'text-red-500'}`}>{value || '(empty)'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                              <h5 className="font-bold text-purple-800 mb-2">CTWA FIELDS (Meta Ad Data)</h5>
                              <div className="space-y-1">
                                {Object.entries(investigatorResult.webhook_audit.parsed_ctwa_fields || {}).map(([key, value]) => (
                                  <div key={key} className={`flex justify-between py-1 px-2 rounded ${key === 'CtwaClid' ? (value ? 'bg-green-100 border border-green-400' : 'bg-red-100 border border-red-400') : 'border-b border-dashed'}`}>
                                    <span className="font-medium text-purple-600">{key}:</span>
                                    <span className={`font-mono ${value ? 'text-green-700 font-bold' : 'text-red-500'}`}>{value || '(NOT SENT)'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(JSON.stringify(investigatorResult.full_lead_data, null, 2)); toast.success('Lead data copied'); }}>
                        <Copy className="h-4 w-4 mr-2" /> Copy Full Data
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* DIAGNOSE TAB */}
            {investigatorTab === 'diagnose' && (
              <div className="space-y-4">
                {isDiagnosing ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
                    <span className="ml-3">Diagnosing lead source issues...</span>
                  </div>
                ) : diagnosticData ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-700">{diagnosticData.total_leads}</p>
                        <p className="text-xs text-blue-600">Total</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-700">{diagnosticData.summary?.total_meta_whatsapp || 0}</p>
                        <p className="text-xs text-green-600">META_WA</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-700">{diagnosticData.summary?.total_website || 0}</p>
                        <p className="text-xs text-amber-600">WEBSITE</p>
                      </div>
                    </div>
                    <div className="bg-gray-50 border rounded-lg p-4">
                      <h4 className="font-medium mb-2">Source Breakdown</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(diagnosticData.by_source || {}).map(([source, count]) => (
                          <div key={source} className="flex justify-between py-1 border-b border-dashed">
                            <span className="text-gray-600">{source}:</span>
                            <span className="font-mono font-bold">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {diagnosticData.potential_issues?.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="font-medium mb-2 text-red-800">Potential Issues ({diagnosticData.potential_issues.length})</h4>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {diagnosticData.potential_issues.slice(0, 10).map(lead => (
                            <div key={lead.id} className="bg-white rounded p-2 text-sm border">
                              <div className="flex justify-between"><span className="font-medium">{lead.name}</span><span className="text-gray-500">{lead.mobile}</span></div>
                              <div className="text-xs text-red-600">{lead.issue}</div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleFixSourceIssues('system_created', true)} disabled={isFixing}>Preview Fix</Button>
                          <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => handleFixSourceIssues('system_created', false)} disabled={isFixing}>
                            {isFixing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Fix {diagnosticData.potential_issues.length} Leads
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end">
                      <Button variant="outline" onClick={handleDiagnoseSourceIssues} disabled={isDiagnosing}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${isDiagnosing ? 'animate-spin' : ''}`} /> Refresh
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Click to scan leads for source problems</p>
                    <Button className="mt-4" onClick={handleDiagnoseSourceIssues}>Start Diagnosis</Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsInvestigatorOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Reassign Leads Modal ═══ */}
      <Dialog open={isReassignOpen} onOpenChange={setIsReassignOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto" data-testid="reassign-leads-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-indigo-600" />
              Reassign Leads
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* From / To Executives */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">From Executive</Label>
                <Select value={reassignFrom} onValueChange={v => { setReassignFrom(v); setReassignPreview(null); setReassignResult(null); setReassignCity(''); }}>
                  <SelectTrigger data-testid="reassign-from-select"><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {reassignExecs.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fromExec && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {fromCities.map(c => <span key={c} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{c}</span>)}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">To Executive</Label>
                <Select value={reassignTo} onValueChange={v => { setReassignTo(v); setReassignPreview(null); setReassignResult(null); }}>
                  <SelectTrigger data-testid="reassign-to-select"><SelectValue placeholder="Select target" /></SelectTrigger>
                  <SelectContent>
                    {reassignExecs.filter(e => e.id !== reassignFrom).map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {toExec && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {toCities.map(c => <span key={c} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] rounded">{c}</span>)}
                  </div>
                )}
              </div>
            </div>

            {/* All Leads Toggle */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
              <Checkbox
                id="reassign-all"
                checked={reassignAll}
                onCheckedChange={v => { setReassignAll(v); setReassignPreview(null); }}
                data-testid="reassign-all-checkbox"
              />
              <Label htmlFor="reassign-all" className="text-sm cursor-pointer">
                Reassign <strong>all leads</strong> from source executive
              </Label>
            </div>

            {/* Filters (hidden when All Leads is checked) */}
            {!reassignAll && (
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg border">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Filters (optional)</p>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">From Date</Label>
                    <Input type="date" value={reassignDateFrom} onChange={e => { setReassignDateFrom(e.target.value); setReassignPreview(null); }} data-testid="reassign-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">To Date</Label>
                    <Input type="date" value={reassignDateTo} onChange={e => { setReassignDateTo(e.target.value); setReassignPreview(null); }} data-testid="reassign-date-to" />
                  </div>
                </div>

                {/* City Filter */}
                {fromCities.length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">City</Label>
                    <Select value={reassignCity} onValueChange={v => { setReassignCity(v === '__all__' ? '' : v); setReassignPreview(null); }}>
                      <SelectTrigger data-testid="reassign-city-select"><SelectValue placeholder="All cities" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All cities</SelectItem>
                        {fromCities.map(c => (
                          <SelectItem key={c} value={c}>
                            {c}
                            {toExec && !toCities.some(tc => tc.toLowerCase() === c.toLowerCase()) && (
                              <span className="ml-1 text-red-500 text-xs">(not in target)</span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Status Filter */}
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Status</Label>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                    {LEAD_STATUSES.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleStatus(s)}
                        className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                          reassignStatuses.includes(s)
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                        }`}
                        data-testid={`reassign-status-${s.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* City Mismatch Warning */}
            {reassignCity && toExec && !toCities.some(tc => tc.toLowerCase() === reassignCity.toLowerCase()) && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  <strong>{toExec.name}</strong> does not have <strong>{reassignCity}</strong> in their assigned cities.
                  Leads from this city will be skipped during reassignment.
                </p>
              </div>
            )}

            {/* Preview Button */}
            <Button
              onClick={handlePreviewReassign}
              disabled={!reassignFrom || !reassignTo || isPreviewingReassign}
              variant="outline"
              className="w-full"
              data-testid="reassign-preview-btn"
            >
              {isPreviewingReassign ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              Preview Matching Leads
            </Button>

            {/* Preview Results */}
            {reassignPreview && (
              <div className="bg-white border rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-blue-50 rounded-lg p-2">
                    <p className="text-xl font-bold text-blue-700">{reassignPreview.matching_count}</p>
                    <p className="text-[10px] text-blue-600 uppercase">Total Matched</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2">
                    <p className="text-xl font-bold text-green-700">{reassignPreview.matching_count - (reassignPreview.city_mismatch || []).length}</p>
                    <p className="text-[10px] text-green-600 uppercase">Eligible</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-xl font-bold text-gray-500">{reassignPreview.to_user_cities?.length || 0}</p>
                    <p className="text-[10px] text-gray-500 uppercase">Target Cities</p>
                  </div>
                </div>

                {reassignPreview.city_breakdown?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">City Breakdown (source exec)</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {reassignPreview.city_breakdown.map(c => {
                        const inTarget = (reassignPreview.to_user_cities || []).some(tc => tc.toLowerCase() === (c.city || '').toLowerCase());
                        return (
                          <div key={c.city} className={`flex justify-between items-center text-xs px-2 py-1 rounded border ${inTarget ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                            <span className="text-gray-700">{c.city}</span>
                            <span className="font-mono font-bold">{c.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Result */}
            {reassignResult && (
              <div className={`p-4 rounded-lg border ${reassignResult.reassigned_count > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className={`h-5 w-5 ${reassignResult.reassigned_count > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="font-semibold text-sm">
                    {reassignResult.reassigned_count > 0
                      ? `${reassignResult.reassigned_count} lead(s) reassigned to ${reassignResult.to_name}`
                      : 'No leads were reassigned'}
                  </span>
                </div>
                {reassignResult.city_mismatch_count > 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    {reassignResult.city_mismatch_count} lead(s) skipped due to city mismatch
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsReassignOpen(false)}>Cancel</Button>
            <Button
              onClick={handleBulkReassign}
              disabled={!reassignFrom || !reassignTo || isReassigning || !!reassignResult}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="reassign-confirm-btn"
            >
              {isReassigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRightLeft className="h-4 w-4 mr-2" />}
              {isReassigning ? 'Reassigning...' : 'Reassign Leads'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ City View Modal ═══ */}
      <Dialog open={isCityViewOpen} onOpenChange={setIsCityViewOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh]" data-testid="city-view-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              Cities per Sales Executive
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-3 py-4">
            {isLoadingEmployees ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : employees.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No sales executives found</p>
              </div>
            ) : (
              employees.map((emp) => (
                <div key={emp.id} className="p-3 bg-white rounded-lg border hover:shadow-sm transition-shadow">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
                      {emp.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{emp.name}</div>
                      <div className="text-xs text-gray-500">{emp.email}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(emp.assigned_cities || []).length > 0 ? (
                      (emp.assigned_cities || []).map((city) => (
                        <span key={city} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">{city}</span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400 italic">No cities assigned</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setIsCityViewOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Force Complete Inspection Modal (P0 escape hatch) ═══ */}
      <Dialog open={isForceCompleteOpen} onOpenChange={(open) => { setIsForceCompleteOpen(open); if (!open) resetForceComplete(); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto" data-testid="force-complete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              Force Complete Inspection
            </DialogTitle>
            <p className="mt-2 text-xs text-slate-600">
              Use this only when the mechanic app refuses to enable the &quot;Complete Inspection&quot; button
              for a real inspection that has been physically completed. This bypasses ALL client-side checks
              (categories, OBD scan, rescan flags). Action is irreversible and audited with your name + reason.
            </p>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Search */}
            <div className="space-y-1.5">
              <Label htmlFor="fc-search">Inspection ID or Vehicle Number</Label>
              <div className="flex gap-2">
                <Input
                  id="fc-search"
                  data-testid="fc-search-input"
                  value={fcSearchTerm}
                  onChange={(e) => setFcSearchTerm(e.target.value)}
                  placeholder="e.g. MH01BY0679 or 52f07e6a-0a07-4776-84ae-adf7ba351040"
                  onKeyDown={(e) => e.key === 'Enter' && handleFcSearch()}
                />
                <Button onClick={handleFcSearch} disabled={fcSearching} data-testid="fc-search-btn">
                  {fcSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  <span className="ml-2">Find</span>
                </Button>
              </div>
            </div>

            {/* Found inspection details */}
            {fcInspection && (
              <div className="rounded-lg border border-slate-300 bg-slate-50 p-4 space-y-1.5 text-sm" data-testid="fc-inspection-card">
                <FcRow k="ID" v={fcInspection.id} mono />
                <FcRow k="Vehicle" v={fcInspection.vehicle_number || fcInspection.vehicleNumber || '—'} />
                <FcRow k="Customer" v={fcInspection.customer_name || fcInspection.customerName || '—'} />
                <FcRow k="Mobile" v={fcInspection.customer_mobile || fcInspection.customerMobile || '—'} />
                <FcRow k="Status" v={fcInspection.inspection_status} highlight />
                <FcRow k="Mechanic" v={fcInspection.mechanic_name || fcInspection.mechanicName || '—'} />
                <FcRow k="OBD scanning" v={fcInspection.obd_scanning_enabled === false ? 'OFF' : 'ON'} />
                <FcRow k="OBD completed" v={String(!!fcInspection.obd_completed)} />
                <FcRow k="Rescan enabled" v={String(!!fcInspection.obd_rescan_enabled)} />
              </div>
            )}

            {/* Already terminal banner */}
            {fcAlreadyTerminal && (
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start gap-2" data-testid="fc-already-complete-banner">
                <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Already in terminal state</div>
                  <div className="text-xs">This inspection is {fcInspection?.inspection_status}. No action required.</div>
                </div>
              </div>
            )}

            {/* Reason + force button */}
            {fcInspection && !fcAlreadyTerminal && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="fc-reason">Reason (required, will be permanently logged)</Label>
                  <textarea
                    id="fc-reason"
                    data-testid="fc-reason-input"
                    value={fcReason}
                    onChange={(e) => setFcReason(e.target.value)}
                    placeholder='e.g. "Mechanic finished QA on-site, app stuck on stale OBD rescan flag, customer waiting." (min 10 chars)'
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                {fcConfirmShown && (
                  <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 flex items-start gap-2" data-testid="fc-confirm-banner">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold">This action is irreversible</div>
                      <div className="text-xs">
                        You are about to mark inspection <strong>{fcInspection.id}</strong> as INSPECTION_COMPLETED.
                        AI report generation will trigger automatically. Click again to confirm.
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleFcForceComplete}
                  disabled={fcSubmitting}
                  className="w-full bg-rose-600 hover:bg-rose-700"
                  data-testid="fc-execute-btn"
                >
                  {fcSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                  {fcConfirmShown ? 'CONFIRM — Force Complete Now' : 'Force Complete'}
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsForceCompleteOpen(false); resetForceComplete(); }} data-testid="fc-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ Service Lead Activity Lookup ═══ */}
      <Dialog open={svcActivityOpen} onOpenChange={(v) => {
        setSvcActivityOpen(v);
        if (!v) {
          setSvcMobile(''); setSvcLeads([]); setSvcSelectedId(null); setSvcSelectedDetail(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
                       data-testid="svc-activity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-4 w-4 text-blue-600" /> Fetch Service Lead Activity
            </DialogTitle>
            <p className="text-xs text-gray-500">
              Enter a customer mobile to fetch all their service leads + full activity timeline.
            </p>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="10-digit mobile e.g. 9999911111"
                value={svcMobile}
                onChange={e => setSvcMobile(e.target.value.replace(/[^\d]/g, ''))}
                onKeyDown={async e => {
                  if (e.key === 'Enter' && svcMobile.length >= 10) {
                    e.preventDefault();
                    setSvcLookupLoading(true);
                    setSvcLeads([]); setSvcSelectedId(null); setSvcSelectedDetail(null);
                    try {
                      const r = await serviceLeadsApi.byCustomer(svcMobile);
                      setSvcLeads(r.data?.rows || []);
                      if ((r.data?.rows || []).length === 0) {
                        toast.info('No service leads found for this mobile');
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Lookup failed');
                    } finally { setSvcLookupLoading(false); }
                  }
                }}
                data-testid="svc-activity-mobile"
              />
              <Button
                onClick={async () => {
                  if (svcMobile.length < 10) {
                    toast.error('Enter a valid 10-digit mobile');
                    return;
                  }
                  setSvcLookupLoading(true);
                  setSvcLeads([]); setSvcSelectedId(null); setSvcSelectedDetail(null);
                  try {
                    const r = await serviceLeadsApi.byCustomer(svcMobile);
                    setSvcLeads(r.data?.rows || []);
                    if ((r.data?.rows || []).length === 0) {
                      toast.info('No service leads found for this mobile');
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Lookup failed');
                  } finally { setSvcLookupLoading(false); }
                }}
                disabled={svcLookupLoading || svcMobile.length < 10}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="svc-activity-fetch"
              >
                {svcLookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fetch'}
              </Button>
            </div>

            {/* Lead picker */}
            {svcLeads.length > 0 && (
              <div>
                <Label className="text-xs">
                  {svcLeads.length} lead{svcLeads.length !== 1 ? 's' : ''} found — pick one:
                </Label>
                <div className="space-y-1.5 mt-1 max-h-64 overflow-y-auto" data-testid="svc-activity-leads">
                  {svcLeads.map((l) => (
                    <button
                      key={l.id}
                      onClick={async () => {
                        setSvcSelectedId(l.id);
                        setSvcSelectedDetail(null);
                        try {
                          const r = await serviceLeadsApi.get(l.id);
                          setSvcSelectedDetail(r.data);
                        } catch (err) {
                          toast.error('Failed to load activity');
                        }
                      }}
                      className={`w-full text-left rounded border p-2 transition-all ${
                        svcSelectedId === l.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 bg-white'
                      }`}
                      data-testid={`svc-activity-pick-${l.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">
                          {l.package_name || 'No package'}
                        </span>
                        <Badge className="text-[10px]">{l.status}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-gray-500 mt-0.5">
                        <span className="truncate">
                          {(l.vehicle?.brand || '')} {(l.vehicle?.model || '')}
                          {l.vehicle?.rc_number ? ` (${l.vehicle.rc_number})` : ''}
                        </span>
                        <span className="flex-shrink-0">
                          {l.final_price != null ? `${activeCurrencySymbol()}${Math.round(l.final_price).toLocaleString('en-IN')}` : '—'}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {l.created_at ? new Date(l.created_at).toLocaleString('en-IN') : ''}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Activity timeline */}
            {svcSelectedId && (
              <div className="border-t pt-3">
                <Label className="text-xs">Activity timeline</Label>
                {!svcSelectedDetail ? (
                  <div className="flex items-center text-gray-400 text-sm mt-2">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
                  </div>
                ) : (
                  <div className="space-y-2 mt-2 max-h-72 overflow-y-auto pr-1">
                    {(svcSelectedDetail.activities || []).slice().reverse().map((a) => (
                      <div key={a.id} className="border-l-2 border-blue-200 pl-3 py-1.5">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="font-medium uppercase tracking-wide">
                            {(a.type || '').replace(/_/g, ' ')}
                          </span>
                          <span>{a.at ? new Date(a.at).toLocaleString('en-IN') : ''}</span>
                        </div>
                        {a.note && <div className="text-gray-700 mt-0.5 text-sm">{a.note}</div>}
                        {a.outcome && !a.note && <div className="text-gray-700 mt-0.5 text-sm">Outcome: {a.outcome}</div>}
                        {a.by && <div className="text-[11px] text-gray-400 mt-0.5">by {a.by}</div>}
                      </div>
                    ))}
                    {(svcSelectedDetail.activities || []).length === 0 && (
                      <div className="text-xs text-gray-400">No activity recorded for this lead.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSvcActivityOpen(false)}
                    data-testid="svc-activity-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Tiny helper for the inspection-detail rows in the Force Complete modal.
// Defined outside the component to avoid recreating on each render.
function FcRow({ k, v, mono, highlight }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-slate-500">{k}</span>
      <span className={`text-right break-all ${mono ? 'font-mono text-xs' : ''} ${highlight ? 'rounded bg-amber-100 px-2 py-0.5 font-semibold' : ''}`}>
        {v}
      </span>
    </div>
  );
}
import { activeCurrencySymbol } from '@/utils/currency';
